/**
 * Crazy Cribbage - CoachController
 * Manages Coach Mode hint display, discard preview potential, and expected hand point analysis.
 */

import { CribbageAI } from '../ai/CribbageAI.js';

export class CoachController {
  /**
   * Computes min, max, and exact average show points for a candidate keep against all unseen starters.
   * @param {Array} keep The cards kept in hand
   * @param {Set<string>} deadIds Cards already seen
   * @param {Object} [options]
   * @returns {{min: number, max: number, avg: number}}
   */
  static analyzeKeepEV(keep, deadIds, options = {}) {
    const starter = options.starter || (typeof window !== 'undefined' && window.state ? window.state.starter : null);
    const fullDeck = options.fullDeck || (typeof window !== 'undefined' ? window.FULL_DECK : null);
    if (CribbageAI && typeof CribbageAI.analyzeKeepEV === 'function') {
      return CribbageAI.analyzeKeepEV(keep, deadIds, starter, fullDeck);
    }
    return { min: 0, max: 0, avg: 0 };
  }

  /**
   * Updates or hides the Coach Mode projection banner based on hover or selected discards.
   * @param {Array} [hoverDiscards] Optional cards currently being hovered
   * @param {Object} [context]
   */
  static updateCoachDisplay(hoverDiscards, context = {}) {
    if (typeof document === 'undefined' && !context.box) return;

    const box = context.box || (typeof document !== 'undefined' ? document.getElementById('coachBox') : null);
    if (!box) return;

    const settings = context.settings || (typeof window !== 'undefined' ? window.settings : null) || {};
    const state = context.state || (typeof window !== 'undefined' ? window.state : null) || {};
    const cardLabel = context.cardLabel || (typeof window !== 'undefined' && window.cardLabel ? window.cardLabel : (c => (c ? `${c.rank}${c.suit}` : '')));

    if (settings.coach !== 'on' || state.phase !== 'discard' || !state.youHand || state.youHand.length === 0) {
      box.classList.add('hidden');
      box.innerHTML = '';
      return;
    }

    const count = (settings && settings.playerCount) || 2;
    const is7Card = Boolean(settings && settings.opt7CardCribbage && count === 2);
    let discardN = count >= 3 ? 1 : (is7Card ? 2 : (settings.deal === 'super' ? 3 : 2));
    if (settings && (settings.optSpeedoCribbage || settings.optTwistedCribbage) && count === 2) {
      const curSeat = state.currentHumanDiscard || 'p1';
      discardN = (curSeat === state.dealer) ? (settings.deal === 'super' ? 6 : 4) : (settings.deal === 'super' ? 3 : 2);
    }
    const targetKeepN = is7Card ? 5 : (settings.deal === 'super' ? 6 : 4);

    // Determine candidate discards: hover takes priority, then active selections
    const selectedIds = state.selected || [];
    let discards = hoverDiscards || state.youHand.filter(c => selectedIds.includes(c.id));

    // If hovering an unselected card and we have slots left, simulate adding it
    if (discards.length !== discardN) {
      box.classList.remove('hidden');
      box.innerHTML = `
        <div class="coach-banner">
          <div class="coach-title">Coach Mode</div>
          <div class="coach-hint">Select ${discardN} cards to discard (or hover) to preview keep potential.</div>
        </div>`;
      return;
    }

    const discIds = new Set(discards.map(c => c.id));
    const candidateKeep = state.youHand.filter(c => !discIds.has(c.id));

    if (candidateKeep.length !== targetKeepN) {
      box.classList.add('hidden');
      return;
    }

    const dead = new Set(state.youHand.map(c => c.id));
    const stats = CoachController.analyzeKeepEV(candidateKeep, dead, {
      starter: state.starter,
      fullDeck: context.fullDeck || (typeof window !== 'undefined' ? window.FULL_DECK : null)
    });
    const isHypothetical = !!hoverDiscards;

    box.classList.remove('hidden');
    box.innerHTML = `
      <div class="coach-banner">
        <div class="coach-title">Coach Mode ${isHypothetical ? '(Preview)' : '(Current Selection)'}</div>
        <div class="coach-stats">
          <div class="stat-item">
            <span class="stat-lbl">Min Hand</span>
            <span class="stat-val">${stats.min}</span>
          </div>
          <div class="stat-item">
            <span class="stat-lbl">Expected Avg</span>
            <span class="stat-val avg">${stats.avg}</span>
          </div>
          <div class="stat-item">
            <span class="stat-lbl">Max Hand</span>
            <span class="stat-val">${stats.max}</span>
          </div>
        </div>
        <div class="coach-hint">Keeping: ${candidateKeep.map(c => cardLabel(c)).join(' ')}</div>
      </div>`;
  }

  updateCoachDisplay(hoverDiscards, context) {
    return CoachController.updateCoachDisplay(hoverDiscards, context);
  }

  analyzeKeepEV(keep, deadIds, options) {
    return CoachController.analyzeKeepEV(keep, deadIds, options);
  }
}
